import { ActionButton, ButtonModel, IDropdownOption, IconRegularSearch, Pager } from '@notino/react-styleguide'
import React, { PropsWithChildren, useContext, useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

// utils
import { MyLocationContext } from '../../utils/myLocationProvider'
import { pushToDataLayer } from '../../utils/dataLayer'
import { getListPaginatingEvent, getSelectItemEvent, getSortingSetEvent, getToggleMapEvent, getViewItemListEvent } from '../../utils/dataLayerEvents'
import { GA_EVENTS_DELAY, SALONS_GRID_ELEMENTS_PER_PAGE, SALONS_PER_PAGE, SORTING_OPTION } from '../../utils/enums'
import { AppContext } from '../../utils/appProvider'
import { getLinkAttributes, PAGE_LINKS } from '../../utils/helper'

// hooks
import useMessages from '../../hooks/useMessages'
import useSortingOptions from '../../hooks/useSortingOptions'

// styles
import * as SC from './SalonsViewStyles'
import { DesktopOnly, MobileOnly } from '../../styles/helpers'
import { BREAKPOINTS_INTS } from '../../styles/constants'

// types
import {
	Salon,
	SalonsResponse,
	LoadSalonsFromFiltersParams,
	SalonsListPageQuery,
	SalonsListPageSetQuery,
	SalonsCategoryPageQuery,
	DefaultCityType,
	CategoryResponse,
	SalonsCategoryPageSetQuery,
	CmsAppPromoData,
	CmsSalonListSmallBannerData,
	CurrencyCode,
	CmsSalonsMetadata
} from '../../types/types'

// components
import SalonDetail from '../SalonDetail/SalonDetail'
import SalonDetailPlaceholder from '../SalonDetailPlaceholder/SalonDetailPlaceholder'
import SalonsMap from '../SalonsMap/SalonsMap'
import AppPromoSection from '../../pages/HomePage/components/AppPromoSection/AppPromoSection'
import RichText from '../RichText/RichText'
import VisuallyHidden from '../../atoms/VisuallyHidden/VisuallyHidden'

// assets
import ListIcon from '../../assets/icons/ListIcon'
import MapIcon from '../../assets/icons/MapIcon'

const BANNER_INDEX = 3

// props
type SalonsViewProps = {
	salons: Salon[]
	pagination: SalonsResponse['pagination']
	loadSalonsFromLoadMoreButton: () => void
	loadSalonsFromFilters: (params: LoadSalonsFromFiltersParams) => void
	query: SalonsListPageQuery | SalonsCategoryPageQuery
	setQuery: SalonsListPageSetQuery | SalonsCategoryPageSetQuery
	initialLoading: boolean
	salonsLoading: boolean
	buttonLoading: boolean
	defaultCity: DefaultCityType | undefined
	allowedReservations: boolean
	categoryData?: CategoryResponse
	categorySlug?: string
	currencyCode: CurrencyCode
	cmsData: {
		appPromoData: CmsAppPromoData
		smallBannerData: CmsSalonListSmallBannerData
		metaData: CmsSalonsMetadata
	}
	lat: number | undefined
	lon: number | undefined
}

// component
const SalonsView = (props: PropsWithChildren<SalonsViewProps>) => {
	const {
		salons,
		loadSalonsFromFilters,
		pagination,
		loadSalonsFromLoadMoreButton,
		query,
		setQuery,
		salonsLoading,
		buttonLoading,
		initialLoading,
		defaultCity,
		allowedReservations,
		categoryData,
		categorySlug,
		currencyCode,
		cmsData,
		lat,
		lon
	} = props

	const { locale } = useIntl()
	const { messages } = useMessages()
	const { assetsPath, productionEnvironment } = useContext(AppContext)
	const { myLocation } = useContext(MyLocationContext)

	// sorting
	const [currentSortingOption, sortingOptions] = useSortingOptions(query)

	// map view — keep toggles mounted across switches so focus stays on the control;
	// announce the change only via a polite live region (no section focus / querySelector).
	const { isMapView } = query
	const [viewStatusMessage, setViewStatusMessage] = useState('')

	const toggleMapView = () => {
		const nextIsMapView = !isMapView
		setViewStatusMessage(nextIsMapView ? messages['Showing salons on map'] : messages['Showing list of salons'])
		setQuery({ isMapView: nextIsMapView ? true : undefined })

		// push to dataLayer
		const event = getToggleMapEvent(!!isMapView)
		pushToDataLayer(event)
	}

	// filters
	const handleOptionSelect = (option: IDropdownOption) => {
		loadSalonsFromFilters({ orderBy: option.id as SORTING_OPTION })

		// push to dataLayer
		const event = getSortingSetEvent({ option })
		pushToDataLayer(event)
	}

	// ga event
	useEffect(() => {
		// timeout so the dataLayer is updated after the page_view event is sent (in AppLayout)
		setTimeout(() => {
			const event = getViewItemListEvent({ currentPage: query.page, currentSortingOptionName: `${currentSortingOption.id}`, salons })
			pushToDataLayer(event)
		}, GA_EVENTS_DELAY)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	// pagination
	const handlePageChange = (page: number) => {
		setQuery({ ...query, page })
		// push to dataLayer
		const event = getListPaginatingEvent({ page })
		pushToDataLayer(event)
		window.scrollTo({ top: 0, behavior: 'smooth' })
	}

	/**
	 * Temporary hide reaming salons count in the button
	 * https://goodrequest.atlassian.net/browse/NOT-11611
	 */
	// const remainingSalonsCount = pagination.totalCount - pagination.page * pagination.limit
	// const showMoreButtonText = formatMessage(
	// 	{ id: 'Show more salons ({remainingSalonsCount})', defaultMessage: 'Show more salons ({remainingSalonsCount})' },
	// 	{ remainingSalonsCount }
	// )
	const showMoreButtonText = messages['Show more salons']

	const showLoadingSkeletons = salonsLoading && !buttonLoading

	// salons
	const salonsToRender = salons.map((salon, index) => {
		// NOTE: list position is index of salon in the list of ALL available salons
		const pageOffset = (query.page - 1) * SALONS_PER_PAGE
		const listPosition = index + 1 + pageOffset

		const isSalonsCategoryPageQuery = 'avResTimeSlotDayPart' in query

		const itemHref = PAGE_LINKS['/salons/:salonSlug'](locale, salon.seoSlugName, {
			currentCategoryID: categoryData?.category.id,
			avResTimeSlotDayPart: isSalonsCategoryPageQuery ? query.avResTimeSlotDayPart : undefined,
			avResTimeSlotDate: isSalonsCategoryPageQuery ? query.avResTimeSlotDateFrom || query.avResTimeSlotDate : undefined
		})

		const handleItemClick = () => {
			pushToDataLayer(getSelectItemEvent({ listPosition, salon, listName: 'salons_list' }))
		}

		return <SalonDetail salon={salon} key={salon.id} imageLoading={index < 4 ? undefined : 'lazy'} href={itemHref} onClick={handleItemClick} />
	})

	// banner src
	const { image: promoBanner } = cmsData.smallBannerData

	// add banners to salons
	const bannersIndexes: number[] = [] // banner is always on 4th places of "page", so [3, 15, 27, 39, ...]
	for (let index = 0; index < (query.page || 1); index += 1) {
		const itemsPerPage = SALONS_GRID_ELEMENTS_PER_PAGE
		bannersIndexes.push(itemsPerPage * index + BANNER_INDEX)
	}

	bannersIndexes.forEach((index) => {
		// prepare banner
		const bannerPicture = (
			<SC.PromoBannerPicture>
				{/* WebP verzie */}
				{/* TODO: Aktualne to moze byt empty string, pretoze z CMSka nam zatial WepP varianty nechodia */}
				{promoBanner.desktopWebP && <source srcSet={promoBanner.desktopWebP} type='image/webp' media={`(min-width: ${BREAKPOINTS_INTS.lg}px)`} />}
				{promoBanner.mobileWebP && <source srcSet={promoBanner.mobileWebP} type='image/webp' media={`(max-width: ${BREAKPOINTS_INTS.lg - 1}px)`} />}

				{/* JPEG fallback */}
				<source srcSet={promoBanner.desktop} media={`(min-width: ${BREAKPOINTS_INTS.lg}px)`} />
				<source srcSet={promoBanner.mobile} media={`(max-width: ${BREAKPOINTS_INTS.lg - 1}px)`} />

				{/* Fallback obrázok */}
				<img src={promoBanner.desktop} width={652} height={504} alt={promoBanner.alt} loading='lazy' />
			</SC.PromoBannerPicture>
		)

		const banner = promoBanner.link ? (
			<a key={`banner-${index}`} href={promoBanner.link} {...getLinkAttributes(promoBanner.link, productionEnvironment, locale)}>
				{bannerPicture}
			</a>
		) : (
			bannerPicture
		)

		// add banner to salons
		if (salonsToRender.length > index) {
			salonsToRender.splice(index, 0, banner)
		}
	})

	const renderDescriptionContent = () => {
		if (!cmsData.metaData.content.data) {
			return null
		}

		if (cmsData.metaData.content.isRichText) {
			if (cmsData.metaData.content.data.length === 0) {
				return null
			}

			return (
				<div>
					<RichText data={cmsData.metaData.content.data} />
				</div>
			)
		}

		return (
			<div>
				{cmsData.metaData.content.data.descriptionTitle && <SC.DescriptionTitle>{cmsData.metaData.content.data.descriptionTitle}</SC.DescriptionTitle>}
				<SC.Description>
					<FormattedMessage
						id={cmsData.metaData.content.data.descriptionMessageID}
						values={{ p: (chunks) => <p>{chunks}</p>, strong: (chunks) => <strong>{chunks}</strong> }}
					/>
				</SC.Description>
			</div>
		)
	}

	const renderCitiesContent = () => {
		// NOTE: this is currently supported only for category pages (or category city pages)
		// we do not support /salons/all/:citySlug pages for now
		const hasCitiesList = (cmsData.metaData.cities?.length ?? 0) > 0
		const hasCitiesRichText = !!(cmsData.metaData.citiesContent?.isRichText && cmsData.metaData.citiesContent.data.length > 0)

		if (!categorySlug || (!hasCitiesList && !hasCitiesRichText)) {
			return null
		}
		return (
			<>
				<SC.Divider />

				<div>
					{cmsData.metaData.citiesContent?.isRichText && cmsData.metaData.citiesContent.data.length ? (
						<SC.CitiesContent>
							<RichText data={cmsData.metaData.citiesContent.data} />
						</SC.CitiesContent>
					) : null}
					{cmsData.metaData.cities && cmsData.metaData.cities.length > 0 ? (
						<SC.CitiesList>
							{cmsData.metaData.cities?.map((city) => {
								const primaryCitySlug = city.slugs?.[0]?.slugValue
								if (!primaryCitySlug) return null
								return (
									<SC.CitiesItem key={city.id}>
										<SC.CityLink
											href={PAGE_LINKS['/salons/all/:categorySlug/:citySlug'](locale, categorySlug, primaryCitySlug, {
												...query,
												latMy: myLocation?.latMy,
												lonMy: myLocation?.lonMy
											})}
										>
											{city.cityLabel}
										</SC.CityLink>
									</SC.CitiesItem>
								)
							})}
						</SC.CitiesList>
					) : null}
				</div>
			</>
		)
	}

	const renderServicesContent = () => {
		const { servicesContent } = cmsData.metaData
		if (!servicesContent?.isRichText) {
			return null
		}

		if (servicesContent.data?.length === 0) {
			return null
		}

		return <RichText data={servicesContent.data} />
	}

	const loadMore = () => {
		loadSalonsFromLoadMoreButton()
	}

	const loadingSkeletons = Array.from({ length: SALONS_GRID_ELEMENTS_PER_PAGE }, (_, i) => {
		return <SalonDetailPlaceholder key={i} initialLoading={initialLoading} />
	})

	const viewToggleLabel = isMapView ? messages['Show as list'] : messages['Show on map']
	const viewToggleIcon = isMapView ? <ListIcon /> : <MapIcon />
	const showMobileViewToggle = isMapView || salons.length > 0

	return (
		<>
			<VisuallyHidden as='div' role='status' aria-live='polite' aria-atomic='true'>
				{viewStatusMessage}
			</VisuallyHidden>

			{/* Persistent desktop toggle — same button node across list/map so focus is not lost and AT only hears the live region */}
			<SC.SortingAndPaginationDesktop>
				<SC.ToggleMapButton type='button' onClick={toggleMapView}>
					{viewToggleIcon}
					<span>{viewToggleLabel}</span>
				</SC.ToggleMapButton>
				{!isMapView && (
					<SC.SortingSelect
						$assetsPath={assetsPath}
						currentOption={currentSortingOption}
						handleOptionSelect={handleOptionSelect}
						options={sortingOptions}
						borderless
					/>
				)}
			</SC.SortingAndPaginationDesktop>

			{!isMapView && (
				<>
					{/* sorting mobile */}
					<SC.SortingMobile>
						<SC.SortingSelect
							$assetsPath={assetsPath}
							currentOption={currentSortingOption}
							handleOptionSelect={handleOptionSelect}
							options={sortingOptions}
							borderless
						/>
					</SC.SortingMobile>

					<SC.SalonsGridWrapper>
						<SC.SalonsGridOverflow>
							{(initialLoading || showLoadingSkeletons) && <SC.SalonsGridView>{loadingSkeletons}</SC.SalonsGridView>}
							{/**
							 * Maintain server-rendered salons in the DOM during initial client side loading
							 * to ensure search engine crawlers can index the content
							 */}
							{(initialLoading || (!initialLoading && !showLoadingSkeletons)) && (
								<SC.SalonsGridPositionWrapper $initialLoading={initialLoading}>
									{/* salons grid */}
									{salons.length > 0 ? (
										<SC.SalonsGridView>{salonsToRender}</SC.SalonsGridView>
									) : (
										<SC.NoSalonsMessageWrapper>
											<IconRegularSearch color={'icon.disabled'} height='50' width='50' />
											<SC.NoSalonsMessage>{messages['No results found']}</SC.NoSalonsMessage>
										</SC.NoSalonsMessageWrapper>
									)}
								</SC.SalonsGridPositionWrapper>
							)}
						</SC.SalonsGridOverflow>

						{/**
						 * App promo desktop
						 * Display only when reservations are allowed
						 */}
						{allowedReservations && (
							<DesktopOnly $breakpoint={'lg'}>
								<SC.AppPromoSectionWrapper>
									<AppPromoSection cmsData={cmsData.appPromoData} />
								</SC.AppPromoSectionWrapper>
							</DesktopOnly>
						)}

						{/* load more salons button */}
						{!initialLoading && salons.length > 0 && pagination.totalPages > 1 && (
							<SC.BottomNavigation>
								{salons.length > 0 && query.page !== pagination.totalPages && (
									<ActionButton isLoading={buttonLoading} buttonStyle={ButtonModel.Styles.secondary} onClick={loadMore}>
										{showMoreButtonText}
									</ActionButton>
								)}

								<SC.PaginationWrapper>
									<Pager currentPage={query.page} lastPage={pagination.totalPages} onPageChange={handlePageChange} />
								</SC.PaginationWrapper>
							</SC.BottomNavigation>
						)}

						{/**
						 * App promo mobile
						 * The mobile version needs to have a different banner position than the desktop version to maintain scroll position and ensure the user sees the newly loaded salons
						 * Display only when reservations are allowed
						 */}
						{allowedReservations && (
							<MobileOnly $breakpoint={'lg'}>
								<SC.AppPromoSectionWrapper>
									<AppPromoSection cmsData={cmsData.appPromoData} />
								</SC.AppPromoSectionWrapper>
							</MobileOnly>
						)}
					</SC.SalonsGridWrapper>
					<SC.CmsContentWrapper>
						{renderDescriptionContent()}
						{renderCitiesContent()}
						{renderServicesContent()}
					</SC.CmsContentWrapper>
				</>
			)}

			{isMapView && (
				<SC.SalonsMapView>
					<SalonsMap
						query={query}
						currencyCode={currencyCode}
						currentCategoryID={categoryData?.category.id}
						defaultCity={defaultCity}
						lat={lat}
						lon={lon}
					/>
				</SC.SalonsMapView>
			)}

			{/* Sticky mobile toggle — list: centered with text; map: right-aligned icon only */}
			{showMobileViewToggle && (
				<SC.ToggleMapContainerMobile $isMapView={!!isMapView}>
					<SC.ToggleMapButtonMobile type='button' onClick={toggleMapView} $isMapView={!!isMapView}>
						{viewToggleIcon}
						{isMapView ? <VisuallyHidden>{viewToggleLabel}</VisuallyHidden> : viewToggleLabel}
					</SC.ToggleMapButtonMobile>
				</SC.ToggleMapContainerMobile>
			)}
		</>
	)
}

export default SalonsView
